import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useAnalytics } from '../../../contexts/AnalyticsContext/AnalyticsProvider';
import { calculateDateRanges } from '../../../utils/dateUtils';
import { optionAll } from '../constants';
import { getUserLevelCheckIns } from '../queries/getUserLevelCheckins';
import { TeamOption, TRawUserLevelCheckIns } from '../types/checkInQuality';

export type TUserLevelCheckIns = {
  id: string;
  userName: string;
  teamName: string;
  checkins: TCheckInDetails[];
};

export type TCheckInDetails = {
  checkin_id: string; // UUID as string
  ticket_checkin_id: string; // UUID as string
  ticket_id: string; // UUID as string
  description: string;
  title: string;
  check_in_type: string;
  completion_percentage: number;
  previous_completion_percentage: number;
  blockers: string | null;
  wipLinks: string | null;
};
export const useGetUserLevelCheckins = (
  selectedTeam: TeamOption | null,
): {
  userLevelCheckIns: TUserLevelCheckIns[];
  isLoading: boolean;
  error: Error | null;
} => {
  const { selectedPeriod } = useAnalytics();
  const dateRange = useMemo(() => {
    return calculateDateRanges(selectedPeriod.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedPeriod)]);
  const { data, isLoading, error } = useQuery({
    queryKey: [
      'user-level-checkins',
      dateRange.thisPeriodStart,
      dateRange.thisPeriodEnd,
      selectedTeam?.id,
    ],
    queryFn: () =>
      getUserLevelCheckIns(
        dateRange.thisPeriodStart,
        dateRange.thisPeriodEnd,
        selectedTeam?.id === optionAll.id ? undefined : selectedTeam?.id,
      ),
    enabled: !!dateRange.thisPeriodStart && !!dateRange.thisPeriodEnd,
  });
  const result = useMemo(() => {
    return data?.reduce<Record<string, TUserLevelCheckIns>>(
      (
        acc: Record<string, TUserLevelCheckIns>,
        record: TRawUserLevelCheckIns,
      ) => {
        const {
          user_id: userID,
          user_name: userName,
          team_name: teamName,
          ...checkInDetails
        } = record;

        // Create a unique key combining user_id and team_name
        const key = `${userID}:${teamName || ''}`;

        if (!acc[key]) {
          acc[key] = {
            id: userID,
            userName,
            teamName: teamName || '',
            checkins: [],
          };
        }

        acc[key].checkins.push(checkInDetails);
        return acc;
      },
      {},
    );
  }, [data]);
  return {
    userLevelCheckIns: Object.values(result || {}),
    isLoading,
    error,
  };
};
